import { ThemeComponent } from "@/theme/ThemeComponent";
import { Component, Prop, Ref, SimpleEvent } from "@eastsideco/salvo-ts";

import Swiper, { Navigation, Pagination, Scrollbar, SwiperOptions } from 'swiper';
import 'swiper/css/bundle';

/**
 * ----------------------------------------------------------------------------
 * Component: Featured Video
 * - Creates featured video element for used with YouTube, Vimeo & Hosted videos.
 * - Video settings will be set within section settings.
 *
 * Section Example
 * {! component 'FeaturedVideo' with section: section !}
 * ------------------------------------------------------------------------------
 */
@Component
export class ProductGallery extends ThemeComponent  {

    @Ref({required: false}) video!: HTMLVideoElement|HTMLIFrameElement|HTMLDivElement;
    @Ref({required: false}) trigger!: HTMLButtonElement;
    @Ref({required: false}) close!: HTMLButtonElement;
    @Ref({required: false}) popup!: HTMLDivElement;

    @Ref sliderElement!: HTMLDivElement;

    /**
     * Initialise.
     */
    init() {

        if (!this.video) {
            return;
        }

        this.setEventListeners();

        Swiper.use([Navigation, Pagination, Scrollbar]);

        const slider = new Swiper(this.sliderElement, {
            slidesPerView: 1,
            spaceBetween: 0,
            loop: false,
            watchSlidesProgress: true,
            autoHeight: true,
            preloadImages: true,
            lazy: {
                checkInView: true,
                loadPrevNext: true,
            },
            pagination: {
                el: '.swiper-pagination',
                type: 'bullets',
                clickable: true
            },
            breakpoints: {
                1025: {
                    allowTouchMove: false,
                    pagination: false,
                    autoHeight: false
                }
            }
        });

        window.addEventListener('resize', () => {
            slider.update();
        });


    }

    /**
     * Set event listeners.
     * @param type - video type (youtube|vimeo|hosted).
     */
    private setEventListeners() {

        this.$listen(this.trigger, 'click', () => {

            this.handlePopup();

            document.body.addEventListener('keydown', (event) => {
                if (event.key === 'Escape') {
                    this.handleClose();
                }
            });
        });

        this.$listen(this.close, 'click', () => {
            this.handleClose();
        });
    }

    /**
     * Handle video popup open.
     * @param type - video type (youtube|vimeo|hosted).
     */
    private handlePopup() {
        this.popup.classList.add('is-active');
        document.body.style.overflow = 'hidden';
        this.playVideo();
    }

    /**
     * Handle video popup close.
     * @param type - video type (youtube|vimeo|hosted).
     */
    private handleClose() {
        this.popup.classList.remove('is-active');
        document.body.style.overflow = 'auto';
        this.stopVideo();
    }

    /**
     * Play video.
     * @param type - video type (youtube|vimeo|hosted).
     */
    private playVideo() {

        this.handleHostedVideo('play');
    }

    /**
     * Stop video.
     * @param type - video type (youtube|vimeo|hosted).
     */
    private stopVideo() {


        this.handleHostedVideo('stop');
    }

    /**
     * Handle Hosted videos.
     * @param action - play|stop
     */
    private handleHostedVideo(action:string) {
        if (this.video instanceof HTMLVideoElement) {
            if (action === 'play') {
                this.video.play().then(() => {
                }).catch((error: Error) => {
                    this.$log.error(error.message);
                });
            }

            if (action === 'stop') {
                this.video.pause();
            }
        }
    }
}
